<template>
  <div class="w-full min-h-[30vh] flex flex-col justify-between gap-y-4">
    <CropperModal
      :isModal="isShowCropper"
      @onClose="onCropperClose"
      :image="cropperImage"
      @confirm="cropImageConfirm"
    />

    <AskForSaveModal
      :isModal="isShowAskForSaveFile"
      @onClose="onAskForSaveFileClose"
      @confirm="onAskForSaveFileConfirm"
    />

    <NoneOfMatchModal
      :isModal="isShowNoneOfMatchModal"
      @onClose="onNoneOfMatchClose"
      @confirm="onNoneOfMatchConfirm"
    />

    <FilledListModal
      :isModal="isShowFilledListModal"
      @onClose="onFilledListClose"
      @confirm="onFilledListConfirm"
      :list="foundFileList"
    />

    <!-- schema-sourced components. on a normal scenario, activeForm is undefined -->
    <template v-if="(formStore.activeForm?.form ?? []).length > 0">
      <CollapsibleSection
        :key="key"
        :header-label="$t(section.header)"
        :body-class="useLayoutClasses(section?.form_style?.web?.layout)?.['bodyClass']"
        v-for="(section, key) in formStore.activeForm?.form"
      >
        <template :key="key" v-for="(field, key) in section.schema">
          <InfoBar v-if="field.component == 'InfoBar'" :text="field.text" />
        </template>
      </CollapsibleSection>
    </template>

    <div class="flex flex-col gap-y-2 divide-y">
      <div
        v-if="isShowAutoFillFile && onlyPOCAttachment"
        class="flex self-end items-center mb-5"
        @click="onAutoFillFiles"
      >
        <BorderButton :label="$t('autoFillFiles')" :wrapperClass="'!border-navi !text-navi'">
          <template #icon>
            <autoFillFileSvg class="w-4 h-4"></autoFillFileSvg>
          </template>
        </BorderButton>
      </div>
      <template
        v-if="
          formStore.applicantAttachments.length === 0 &&
          formStore.beneficiaryAttachments.length === 0 &&
          formStore.otherAttachments.length === 0
        "
      >
        <AttachmentRow
          :attachment-type="item"
          :key="key"
          v-for="(item, key) in formStore.attachmentsList
            .filter((att) => !formStore.isExtraAttachment(att))
            .sort((a, b) => b.isMandatory - a.isMandatory)"
          class="pt-2"
        >
          <template #icon>
            <div
              v-if="item.hasIntegration && item.integrationDetailsNoFound"
              class="flex items-center justify-center size-5 rounded-full border border-brand-red"
            >
              <span class="text-brand-red text-2xl font-bold">&times;</span>
            </div>
            
            <div
              v-else
              class="rounded-full border size-5 text-xs flex items-center justify-center border-gray-300"
            >
              {{ ++key }}
            </div>
          </template>
          <template #end>
            <UploaderButton
              :attachment-type="item"
              class="text-xs text-blue-500"
              @on-file-select="(file) => handleFileSelect(file, item)"
            >
              {{ $t('selectMultiFile') }}
            </UploaderButton>
          </template>
        </AttachmentRow>
      </template>

      <!-- <template v-if="formStore.beneficiaryAttachments.length > 0"> TODO </template> -->
    </div>
    <div class="flex flex-col ltr:font-sans text-xs text-brand-black-secondary">
      <p>{{ $t('AttachmentMaxSize') }} {{ $t('2MB') }}</p>
      <p>{{ $t('AttachmentAllowedTypes') }} {{ allowedFileTypes }}</p>
    </div>
    <table v-if="formStore.attachments.length > 0" class="table-auto border-collapse">
      <thead>
        <tr class="text-sm">
          <th class="w-1/12 py-2 ps-3 border border-brand-gray-300 text-start"></th>
          <th class="w-4/12 py-2 ps-3 border border-brand-gray-300 text-start">{{ $t('Type') }}</th>
          <th class="w-4/12 py-2 ps-3 border border-brand-gray-300 text-start">
            {{ $t('theName') }}
          </th>
          <th class="w-fit py-2 ps-3 border border-brand-gray-300 text-start"></th>
        </tr>
      </thead>
      <tbody class="text-sm">
        <tr :key="key" v-for="(item, key) in formStore.attachments" class="odd:bg-brand-gray-100">
          <td class="w-1/12 p-1 border border-brand-gray-300">
            <div class="w-full h-full flex justify-start items-center px-3 py-1">
              <faFileImage v-if="item.fileType == 'image/jpeg'" class="w-5" />
              <faFilePdf v-else class="w-6" />
            </div>
          </td>
          <td class="w-4/12 p-1 border border-brand-gray-300">
            <select
              v-model="item.attachmentType.text"
              class="w-full px-2 py-[5px] rounded-sm bg-white border border-s-2 border-s-brand-green border-brand-gray-300 truncate"
              @change="handleAttachmentTypeChange(item)"
            >
              <option
                :key="key"
                :value="attType.localizedName"
                v-for="(attType, key) in formStore.buildAttachmentsList(item)"
              >
                {{ attType.localizedName }}
              </option>
            </select>
          </td>
          <td class="w-4/12 p-1 border border-brand-gray-300">
            <input
              :disabled="!item.enabled"
              type="text"
              v-model="item.name"
              class="w-full px-2 py-1 border-s-2 rounded-sm border border-s-brand-green border-brand-gray-300 outline-none disabled:bg-brand-gray-250 disabled:truncate"
            />
          </td>
          <td class="p-1 px-2 border border-brand-gray-300">
            <div class="h-full flex justify-start items-center gap-x-2">
              <button
                class="w-6 h-6 flex justify-center items-center border rounded border-brand-green bg-brand-green/80 p-1"
                @click.stop.prevent="downloadAttachment(item)"
              >
                <faDownload class="w-3 fill-white" />
              </button>
              <button
                class="w-6 h-6 flex justify-center items-center border rounded border-red-700 bg-red-600 p-1"
                @click.stop.prevent="formStore.deleteAttachment(item)"
              >
                <faTrash class="w-2.5 fill-white" />
              </button>
              <button
                class="w-6 h-6 flex justify-center items-center border rounded border-cyan-700 bg-cyan-600 p-1"
                @click.stop.prevent="editAttachment(item)"
                v-if="
                  validImageTypes.includes(
                    item?.attachmentFile?.type || item.attachmentFileMIMEType
                  )
                "
              >
                <faPenToSquare class="w-3 fill-white" />
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="flex justify-between">
      <slot name="actions">
        <FilledButton class="px-5 py-1.5 text-white" @click="$emit('onBack')">
          {{ $t('back') }}
        </FilledButton>

        <FilledButton class="px-5 py-1.5 text-white" @click="onNext">
          {{ $t('next') }}
        </FilledButton>
      </slot>
    </div>
  </div>
</template>

<script setup>
import faDownload from '@/assets/icons/faDownload.svg?component'
import faFileImage from '@/assets/icons/faFileImage.svg?component'
import faFilePdf from '@/assets/icons/faFilePdf.svg?component'
import faPenToSquare from '@/assets/icons/faPenToSquare.svg?component'
import faTrash from '@/assets/icons/faTrash.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import client_config from '@/assets/json/client_config.json'
import autoFillFileSvg from '@/assets/svgs/auto-fill-file.svg?component'
import BorderButton from '@/components/Buttons/BorderButton.vue'
import FilledButton from '@/components/Buttons/Filled.vue'
import CropperModal from '@/components/Cropper/CropperModal.vue'
import AttachmentRow from '@/components/Form/AttachmentRow.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import UploaderButton from '@/components/Form/UploaderButton.vue'
import useDraftPayloadOverride from '@/composables/draft_payload_override/base.js'
import useFileStore from '@/composables/filesStore/useFileStore.js'
import useFile from '@/composables/useFile'
import useLayoutClasses from '@/composables/useLayoutClasses'
import { useAuthStore } from '@/stores/auth'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import AskForSaveModal from '@/views/files-autofill/AskForSaveModal.vue'
import FilledListModal from '@/views/files-autofill/FilledListModal.vue'
import NoneOfMatchModal from '@/views/files-autofill/NoneOfMatchModal.vue'
import mine from 'mime-types'
import { notify } from 'notiwind'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const emit = defineEmits('onNext')

const formStore = useFormStore()
const i18n = useI18n()
const authStore = useAuthStore()
const isLoading = ref(true)
const globalStore = useGlobalStore()
const route = useRoute()
const localFileStoreUtil = useFileStore()
const isShowCropper = ref(false)
const cropperImage = ref()
const currentAttachment = ref()
const isShowAutoFillFile = ref(false)
const isShowAskForSaveFile = ref(false)
const isShowNoneOfMatchModal = ref(false)
const foundFileList = ref([])
const isShowFilledListModal = ref(false)

isShowAutoFillFile.value = !authStore.isUserTypingCenter || !authStore.isGuestUser

onMounted(async () => {
  await formStore.getAttachmentList()
  await formStore.processAttachmentList()
  await formStore.loadAttachmentTypesByTransactionReasonId()
  await formStore.setIntegrationDetails()
  isLoading.value = false
})

const onlyPOCAttachment = computed(() => {
  const { serviceId } = route.params
  return serviceId == '29' || serviceId == '727'
})

const allowedFileTypes = computed(() => {
  const { mohreWorkPackage, mohreWorkPackageWorkRenew, mohreWorkPackageWorkCancel } =
    client_lookups.ServiceTransactions
  const { serviceId } = route.params

  const isMohre = [
    mohreWorkPackage,
    mohreWorkPackageWorkRenew,
    mohreWorkPackageWorkCancel
  ].includes(serviceId)

  const { attachmentMohreAllowedTypes, attachmentAllowedTypes } = client_config

  return isMohre ? attachmentMohreAllowedTypes : attachmentAllowedTypes
})

const multipleAttachmentTypesAllowed = false

const photoAttachmentTypes = [141, 401, 402, 611]
const validImageTypes = ['image/gif', 'image/jpeg', 'image/png']

const onAutoFillFiles = async () => {
  foundFileList.value = []
  // check is match service benefit files
  const fileTypeList = formStore.attachmentsList.filter((att) => !formStore.isExtraAttachment(att))
  // .filter((item) => item.isMandatory)
  const searchQueryList = fileTypeList.map((fileInfo) =>
    localFileStoreUtil.generateUniqueFileName(fileInfo)
  )

  for (let index = 0; index < searchQueryList.length; index++) {
    const queryObj = searchQueryList[index]
    let result
    if (queryObj.name1) {
      result = { key: 'name1', value: queryObj.name1 }
    } else if (queryObj.name2) {
      result = { key: 'name2', value: queryObj.name2 }
    } else if (queryObj.name3) {
      result = { key: 'name3', value: queryObj.name3 }
    }
    const matchedList = await localFileStoreUtil.queryFileByName(result.key, result.value)
    if (matchedList.length) {
      const matchedOne = matchedList[matchedList.length - 1]
      matchedOne.extract = JSON.parse(matchedOne.extract || {})
      foundFileList.value.push(matchedOne)
    }
  }
  if (foundFileList.value.length) {
    isShowFilledListModal.value = true
  } else {
    isShowNoneOfMatchModal.value = true
  }
}

const downloadAttachment = async (attachment) => {
  if (attachment.id && attachment.token) {
    const blob = await formStore.getAttachmentFileAsBlob(attachment.id, attachment.token)
    attachment.attachmentFile = blob
    useFile().downloadFile(blob, attachment.name + '.' + mine.extension(blob.type))
  } else if (attachment.attachmentFile) {
    useFile().downloadFile(
      attachment.attachmentFile,
      attachment.name + '.' + mine.extension(attachment.attachmentFile.type)
    )
  }
}

const editAttachment = async (attachment) => {
  if (
    validImageTypes.includes(attachment?.attachmentFile?.type || attachment.attachmentFileMIMEType)
  ) {
    let blob =
      attachment?.attachmentFile ||
      (await formStore.getAttachmentFileAsBlob(attachment.id, attachment.token))
    modifyCropper(attachment, blob)
    isShowCropper.value = true
  }
}

// const uploadAttachment = async (attachmentType) => {
//   const { enableDisableAutoLoaderForAttachments } = client_config
//   const {
//     lookupInfoSourceIdsForApplicant,
//     lookupInfoSourceIdsForBenficiary,
//     lookupInfoSourceIdsForMother,
//     lookupInfoSourceIdsForVisa
//   } = client_lookups
//   const {
//     id,
//     request: { identityNumber, personUnifiedNumber, motherUnifiedNumber }
//   } = formStore.draft
//   let response
//   if (
//     enableDisableAutoLoaderForAttachments &&
//     (lookupInfoSourceIdsForApplicant.some((e) => e == attachmentType.lookupInfoSourceId) ||
//       lookupInfoSourceIdsForBenficiary.some((e) => e === attachmentType.lookupInfoSourceId) ||
//       lookupInfoSourceIdsForMother.some((e) => e === attachmentType.lookupInfoSourceId) ||
//       lookupInfoSourceIdsForVisa.some((e) => e === attachmentType.lookupInfoSourceId))
//   ) {
//     const params = {
//       draftId: id,
//       lookupInfoSourceId: attachmentType.lookupInfoSourceId,
//       beneficiaryIdentityNumber: identityNumber || '',
//       personUnifiedNumber,
//       motherUnifiedNumber
//     }
//     response = await formStore.getPrintUAEDocuments(true, params)
//   } else {
//     const { serviceId } = route.params
//     const params = {
//       serviceTransactionId: serviceId,
//       attachmentTypeId: attachmentType.id,
//       beneficiaryIdentityNumber: identityNumber || '',
//       personUnifiedNumber,
//       motherUnifiedNumber
//     }
//     response = await formStore.getPrintUAEDocuments(false, params)
//   }

// TODO: update checkType.
// if (response && response.byteArrayBase64String) {
//   var file = dataURLtoFile(
//     'data:application/pdf;base64,' + response.byteArrayBase64String,
//     'hello.txt'
//   )

//   var attachment = {
//     attachmentFile: file,
//     attachmentThumbnail: file
//   }

//   attachment.originalFileName = attachment.attachmentFile.name
//   if (attachmentType != null) {
//     scope.changeType(attachment, attachmentType, null)
//   }
//   attachmentType.integrationDetailsNoFound = false
//   attachmentType.integrationDetailsFound = true
// } else {
//   attachmentType.integrationDetailsNoFound = true
//   attachmentType.integrationDetailsFound = false
//   attachmentType.hasIntegration = true
// }
// }

const blobToFile = (blob, finedAttachment) => {
  const { originalFileName } = finedAttachment
  const file = new File([blob], originalFileName, {
    lastModified: new Date().getTime(),
    type: finedAttachment.fileType || finedAttachment.attachmentFileMIMEType
  })
  return file
}

const onAskForSaveFileClose = () => {
  isShowAskForSaveFile.value = false
  emit('onNext')
}
const onAskForSaveFileConfirm = () => {
  isShowAskForSaveFile.value = false
  saveFileToIndexedDB()
  emit('onNext')
}
const onNoneOfMatchClose = () => {
  isShowNoneOfMatchModal.value = false
}
const onNoneOfMatchConfirm = () => {
  isShowNoneOfMatchModal.value = false
}

const onFilledListClose = () => {
  isShowFilledListModal.value = false
}

const onFilledListConfirm = async () => {
  isShowFilledListModal.value = false
  for (let index = 0; index < foundFileList.value.length; index++) {
    const item = foundFileList.value[index]
    await handleFileSelect(item.content, item.extract)
  }
}

const checkIsShowAskForSaveFile = () => {
  if (
    (localStorage.getItem('isNotShowAskFillFileModal') === null ||
      localStorage.getItem('isNotShowAskFillFileModal') === 'false') &&
    onlyPOCAttachment.value
  ) {
    isShowAskForSaveFile.value = true
    return true
  }
  return false
}

const saveFileToIndexedDB = () => {
  if (!isShowAutoFillFile.value) {
    return
  }
  // const list = formStore.attachments.filter((item) => item.attachmentType.isMandatory)
  const list = formStore.attachments
  localFileStoreUtil.saveFileToLocal(list)
}

const onCropperClose = () => {
  try {
    const indexToModify = formStore.attachments.findIndex((att) => att == currentAttachment.value)
    const finedAttachment = formStore.attachments[indexToModify]
    delete finedAttachment.token
    formStore.attachments.splice(indexToModify, 1)
  } catch (error) {
    console.error('error in onCropperClose', error)
  }

  isShowCropper.value = false
}

const cropImageConfirm = (newImage) => {
  isShowCropper.value = false

  const indexToModify = formStore.attachments.findIndex((att) => att == currentAttachment.value)
  const finedAttachment = formStore.attachments[indexToModify]
  delete finedAttachment.token
  finedAttachment.attachmentFile = blobToFile(newImage, finedAttachment)
  formStore.attachments.splice(indexToModify, 1, finedAttachment)
}

const modifyCropper = (attachment, realFile) => {
  cropperImage.value && URL.revokeObjectURL(cropperImage.value)
  const blob = URL.createObjectURL(realFile)
  currentAttachment.value = attachment
  cropperImage.value = blob
}

const GeneralCheckForPersonalPhotoICAO = async (attachment) => {
  const result = await formStore.checkForPersonalPhotoICAO(attachment)
  const { ServiceTransactionUsedICAO } = client_config
  const { serviceId: serviceTransactionId } = route.params
  const { errorMessage } = result
  if (errorMessage) {
    if (
      ServiceTransactionUsedICAO.find((c) => c == serviceTransactionId) &&
      errorMessage.toLowerCase() == 'personal_icao_photo_worning_message'
    ) {
      formStore.deleteAttachment(item)
    } else {
      if (
        errorMessage == 'INVALID_PERSONAL_IMAGE_SIZE_AND_RESOLUTION_LESS_THAN_200' ||
        errorMessage == 'INVALID_PERSONAL_IMAGE_CONTENT'
      ) {
        notify(
          {
            group: 'error',
            title: '',
            text: i18n.t(errorMessage)
          },
          6000
        )

        const lastAttachment = formStore.attachments[formStore.attachments.length - 1]
        if (photoAttachmentTypes.includes(lastAttachment.attachmentTypeId)) {
          if (
            errorMessage == 'INVALID_PERSONAL_IMAGE_SIZE_AND_RESOLUTION_LESS_THAN_200' ||
            errorMessage == 'INVALID_PERSONAL_IMAGE_CONTENT'
          ) {
            formStore.deleteAttachment(item)
          }
        }
      } else {
        if (photoAttachmentTypes.includes(attachment.attachmentTypeId)) {
          modifyCropper(attachment, attachment.attachmentFile)
          isShowCropper.value = true
        }
      }
    }
  }
}

const changeType = async (attachment, attachmentType, index) => {
  if (photoAttachmentTypes.includes(attachmentType.id)) {
    await GeneralCheckForPersonalPhotoICAO(attachment)
    formStore.attachments[index].name = attachment.originalFileName
  }
}

const checkFileIsOverSize = (file, attachmentType) => {
  if (file.size >= 2097152) {
    notify(
      {
        group: 'error',
        text: i18n.t('InvalidFileUploaded', { MAX_SIZE: i18n.t('2MB') })
      },
      6000
    )
    return true
  }
}

const handleFileSelect = async (file, attachmentType) => {
  // TODO: validate files against types etc.
  const attachment = {
    // easy to find attachmentFile by id
    xId: Math.floor(Math.random() * 10000),
    attachmentFile: file,
    attachmentThumbnail: file,
    originalFileName: file.name,
    fileType: file.type,
    typeChanged: false,
    attachmentTypeId: attachmentType ? attachmentType.id : ''
  }

  /**
   * perform all validations for this attachmentType
   */
  if (attachmentType) {
    if (!multipleAttachmentTypesAllowed && hasMultipleTypes(attachmentType)) {
      return
    }

    if (hasExceededCountOfType(attachmentType)) {
      return
    }

    if (mimeTypeMismatched(file, attachmentType)) {
      return
    }

    if (checkFileIsOverSize(file, attachmentType)) {
      return
    }

    Object.assign(attachment, {
      attachmentType,
      attachmentTypeId: attachmentType.id,
      typeChanged: true,
      uploadedFileName: 0
    })

    if (
      globalStore.clientConfig?.allowIcaoValidationForAdditionalServices &&
      client_lookups.attachmentTypes.coloredPersonalPhoto
    ) {
      attachment.enabled = false

      await formStore.uploadPhoto(attachment)
      attachment.name = attachment.originalFileName
      attachment.enabled = formStore.isExtraAttachment(attachment)
    }

    if (photoAttachmentTypes.includes(attachmentType.id)) {
      attachment.enabled = false

      // let personAge = formStore.calculateAgeFromDob()
      // const { issuePassport, replacePassport, issuePassportFromOutsideUae } =
      //   client_lookups.ServiceTransactions
      // if (
      //   [issuePassport, replacePassport, issuePassportFromOutsideUae].includes(
      //     globalStore.startedService.transactionId
      //   )
      // ) {
      //   personAge = 1
      // }

      // if (attachmentType.id)
      await formStore.uploadPhoto(attachment)
      attachment.name = attachment.originalFileName
      attachment.enabled = formStore.isExtraAttachment(attachment)
    }
    attachment.name = attachment.originalFileName
  }

  formStore.attachments.push(attachment)
  changeType(attachment, attachmentType, formStore.attachments.length - 1)
  console.info('all good!')
}

/**
 * check for duplicated attachment types
 * checks the attachmentType.id against the ids of
 * those already in the list
 *
 * TODO: implement allowance of replicated image
 * checkAllowReplicatedImage in reverse-soar
 */
const hasMultipleTypes = (attachmentType) => {
  const attachmentTypeIdExists = formStore.attachments?.some(
    (attType) => attType.attachmentType.id === attachmentType.id
  )

  if (attachmentTypeIdExists) {
    notify(
      {
        group: 'error',
        title: i18n.t('errorSetDefaultAddress'),
        text: i18n.t('YouUploadThisPhotoAlready')
      },
      6000
    )
  }

  return attachmentTypeIdExists
}

/**
 * best recreation from source code.
 * I don't know why specific services and attachment types have this logic.
 */
const getAttachmentMaxCount = (attachmentType) => {
  const transactionIds = [
    client_lookups.ServiceTransactions.femaleChildrenNaturalizationRequest,
    client_lookups.ServiceTransactions.citizenWifeNaturalizationRequest,
    client_lookups.ServiceTransactions.naturalizationRequest
  ]

  const attachmentTypes = [
    612, 975, 983, 972, 973, 974, 969, 970, 971, 614, 976, 956, 958, 979, 957, 955, 953, 605, 611,
    954, 959, 968
  ]

  let output = 1

  if (
    transactionIds.includes(formStore.draft.serviceTransactionId) &&
    attachmentTypes.includes(attachmentType.id)
  ) {
    output = attachmentType.maxNumberOfReplicated

    const relativesInformation = formStore.draft.relatiavesInformation || []

    if (attachmentType.id === 612 && relativesInformation.length > 0) {
      output = relativesInformation.length
    }
  }

  return output
}

/**
 * counts the attachment type. if exceeds what is set
 * based on getAttachmentMaxCount, get angry and early return
 */
const hasExceededCountOfType = (attachmentType) => {
  const attachmentTypeMaxCount = getAttachmentMaxCount(attachmentType)
  const matchingCount = formStore.attachments?.filter((att) => attachmentType.id == att.id) || 0
  const countExceeds = matchingCount >= attachmentTypeMaxCount

  if (multipleAttachmentTypesAllowed && countExceeds) {
    notify(
      {
        group: 'error',
        title: i18n.t('errorSetDefaultAddress'),
        text: i18n.t('YouUploadTheMaxOfThisPhoto')
      },
      6000
    )

    return
  }
}

/**
 * TODO: validated the file selected against attachmentType.allowedMimeType
 */
const mimeTypeMismatched = (file, attachmentType) => {}

/**
 * validates the number of attachments against what's allowed to be uploaded
 * TODO: i'll implement this later
 */
// const hasExceededAllowedCount = (attachmentType) => {}

const buildAttachmentName = (attachment) => {
  const { attachmentFile, attachmentType } = attachment

  // if (globalStore.clientConfig.allowIcaoValidationForAdditionalServices)

  // console.log("attachmentType.allowedMimeType", attachmentType.allowedMimeType);
  // if (attachmentType.allowedMimeType){}

  return 'TODO'
}

const onNext = async () => {
  if (formStore.hasAttachmentsToUpload) {
    const canProceed = await formStore.validateAttachmentsList()
    if (!canProceed && !globalStore.showcase.skipValidation) return

    await formStore.uploadAttachments()
    await formStore.cleanupDeletedAttachments()
  }

  const payload = {
    ...formStore.draft,
    serviceTransactionId: globalStore.startedService.serviceTransactionId
  }

  Object.assign(payload, await useDraftPayloadOverride(payload.serviceTransactionId), {
    attachments: formStore.attachments
  })

  await formStore.updateDraft(payload)

  if (checkIsShowAskForSaveFile()) {
    return
  }

  emit('onNext')
  // notify(
  //     {
  //       group: 'success',
  //       title: i18n.t('success'),
  //       text: 'Attchment has been added successfully'
  //     },
  //     6000
  //   )
}

const handleAttachmentTypeChange = (attachment) => {
  attachment.attachmentTypeId = attachment.attachmentType.id
}
</script>

<style lang="scss" scoped></style>
